import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, KPI, Card, Button, Empty } from "@/components/Primitives";
import { BarChart } from "@/components/Charts";
import { StatusPill, leadStageVariant, invoiceStatusVariant, ticketStatusVariant } from "@/components/StatusPill";
import { useNavigate } from "react-router-dom";
import { Plus, Activity, ArrowUpRight, Sparkles, AlertTriangle, CheckCircle2, XCircle, FileText, Users } from "lucide-react";

function gbp(v) {
  const n = v ?? 0;
  const hasPence = Math.round(n * 100) !== Math.round(n) * 100;
  return `£${n.toLocaleString(undefined, { minimumFractionDigits: hasPence ? 2 : 0, maximumFractionDigits: 2 })}`;
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [leads, setLeads] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [ar, setAr] = useState(null);
  const [quoteResponses, setQuoteResponses] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [ov, ls, inv, tk, arr, qs, rf] = await Promise.all([
          api.get("/stats/admin-overview"),
          api.get("/leads"),
          api.get("/invoices"),
          api.get("/tickets"),
          api.get("/stats/ar-aging"),
          api.get("/quotes"),
          api.get("/stats/recent-referrals", { params: { limit: 5 } }),
        ]);
        setData(ov.data);
        setLeads(ls.data || []);
        setInvoices((inv.data || []).slice(0, 5));
        setTickets((tk.data || []).slice(0, 4));
        setAr(arr.data);
        const responded = (qs.data || [])
          .filter((q) => (q.status === "accepted" || q.status === "declined") && q.responded_at)
          .sort((a, b) => (b.responded_at || "").localeCompare(a.responded_at || ""))
          .slice(0, 5);
        setQuoteResponses(responded);
        setReferrals(rf.data || []);
      } catch (e) { console.error(e); }
    })();
  }, []);

  if (!data) return <div className="text-sm text-muted">Loading…</div>;
  const k = data.kpis;

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb="Dashboard · Owner"
        title="Command centre"
        subtitle="Everything that moves, in one place."
        actions={
          <>
            <Button variant="secondary" onClick={() => nav("/leads")}><Sparkles className="w-4 h-4" strokeWidth={1.6} />Leads</Button>
            <Button onClick={() => nav("/leads?new=1")} data-testid="new-lead-cta"><Plus className="w-4 h-4" strokeWidth={2} />New lead</Button>
          </>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger">
        <KPI label="MRR" value={gbp(k.mrr)} sub="Recognised this month" variant="primary" />
        <KPI label="Pipeline value" value={gbp(k.pipeline_value)} sub={`${k.open_leads} open leads · ${k.win_rate}% win`} />
        <KPI label="Active customers" value={k.active_customers} sub="Across all products" />
        <KPI label="Outstanding" value={gbp(k.outstanding)} sub={`${k.open_tickets} open tickets`} variant={k.outstanding > 0 ? "warning" : "success"} />
      </div>

      {/* Quote responses banner */}
      {quoteResponses.length > 0 && <QuoteResponsesCard responses={quoteResponses} onNav={() => nav("/quotes")} />}

      {/* Referrals from customers */}
      <RecentReferralsCard referrals={referrals} onOpen={(rid) => nav(`/leads/${rid}`)} onSeeAll={() => nav("/leads?source=referral")} />

      {/* Middle */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Revenue · last 6 months" className="lg:col-span-2" right={<span className="text-[11px] uppercase tracking-widest text-[hsl(var(--text-muted))]">GBP</span>}>
          <div className="p-5">
            <BarChart data={data.revenue_series || []} prefix="£" />
          </div>
        </Card>
        <Card title="Activity" right={<Activity className="w-4 h-4 text-[hsl(var(--text-muted))]" strokeWidth={1.6} />}>
          <div className="divide-y divide-[hsl(var(--border))]">
            {(data.activity || []).slice(0, 6).map((a) => (
              <div key={a.id} className="px-5 py-3 text-[13px]">
                <div className="font-medium leading-snug">{a.message}</div>
                <div className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5">{a.actor || "System"} · {new Date(a.created_at).toLocaleString()}</div>
              </div>
            ))}
            {(!data.activity || data.activity.length === 0) && <Empty title="No activity yet" />}
          </div>
        </Card>
      </div>

      {/* Lower grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Recent leads" className="lg:col-span-2" right={<button className="text-[12px] text-[hsl(var(--primary))] font-semibold" onClick={() => nav("/leads")}>View all <ArrowUpRight className="inline w-3 h-3" strokeWidth={2}/></button>}>
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))]">
                <th className="text-left font-semibold px-5 py-3">Company</th>
                <th className="text-left font-semibold px-5 py-3">Contact</th>
                <th className="text-left font-semibold px-5 py-3">Stage</th>
                <th className="text-right font-semibold px-5 py-3">Value</th>
              </tr>
            </thead>
            <tbody>
              {leads.slice(0, 6).map((l) => (
                <tr key={l.id} className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-hover))] cursor-pointer" onClick={() => nav("/leads")}>
                  <td className="px-5 py-3 font-medium">{l.company}</td>
                  <td className="px-5 py-3 text-[hsl(var(--text-muted))]">{l.contact}</td>
                  <td className="px-5 py-3"><StatusPill variant={leadStageVariant(l.stage)}>{l.stage}</StatusPill></td>
                  <td className="px-5 py-3 text-right font-mono font-semibold">{gbp(l.est_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card title="Open tickets">
          <div className="divide-y divide-[hsl(var(--border))]">
            {tickets.map((t) => (
              <div key={t.id} className="px-5 py-3 hover:bg-[hsl(var(--surface-hover))] cursor-pointer" onClick={() => nav("/tickets")}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[13px] font-medium truncate">{t.subject}</div>
                    <div className="text-[11px] text-[hsl(var(--text-muted))]">{t.customer_name} · {t.number}</div>
                  </div>
                  <StatusPill variant={ticketStatusVariant(t.status)}>{t.status.replace("_", " ")}</StatusPill>
                </div>
              </div>
            ))}
            {tickets.length === 0 && <div className="px-5 py-6 text-[13px] text-[hsl(var(--text-muted))]">No open tickets.</div>}
          </div>
        </Card>
      </div>

      {/* Invoices strip */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2" title="Latest invoices" right={<button className="text-[12px] text-[hsl(var(--primary))] font-semibold" onClick={() => nav("/invoices")}>View all</button>}>
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))]">
                <th className="text-left font-semibold px-5 py-3">Number</th>
                <th className="text-left font-semibold px-5 py-3">Customer</th>
                <th className="text-left font-semibold px-5 py-3">Status</th>
                <th className="text-left font-semibold px-5 py-3">Issued</th>
                <th className="text-right font-semibold px-5 py-3">Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((i) => (
                <tr key={i.id} className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-hover))]">
                  <td className="px-5 py-3 font-mono font-semibold">{i.number}</td>
                  <td className="px-5 py-3">{i.customer_name}</td>
                  <td className="px-5 py-3"><StatusPill variant={invoiceStatusVariant(i.status)}>{i.status}</StatusPill></td>
                  <td className="px-5 py-3 text-[hsl(var(--text-muted))]">{new Date(i.issued_date).toLocaleDateString()}</td>
                  <td className="px-5 py-3 text-right font-mono font-semibold">{gbp(i.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <ArAgingCard ar={ar} onRowClick={() => nav("/invoices")} />
      </div>
    </div>
  );
}

function ArAgingCard({ ar, onRowClick }) {
  const hasOverdue = (ar?.total_overdue || 0) > 0;
  return (
    <Card
      title="Accounts receivable"
      right={
        hasOverdue ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded bg-rose-500/15 text-rose-600 dark:text-rose-400">
            <AlertTriangle className="w-3 h-3" strokeWidth={2}/>
            {ar?.oldest_days_overdue}d oldest
          </span>
        ) : null
      }
    >
      <div className="p-5 space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">Total owed</div>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-3xl font-mono font-bold tracking-tight">{gbp(ar?.total_outstanding || 0)}</span>
          </div>
          {hasOverdue && (
            <div className="text-[12px] text-rose-600 dark:text-rose-400 mt-1 font-medium">
              {gbp(ar.total_overdue)} overdue · {ar.overdue_count} {ar.overdue_count === 1 ? "invoice" : "invoices"}
            </div>
          )}
        </div>

        <div className="pt-1 border-t border-[hsl(var(--border))] -mx-5" />

        {(ar?.by_customer || []).length === 0 ? (
          <div className="text-[13px] text-[hsl(var(--text-muted))] py-2">
            All clear. Nothing outstanding.
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">Top outstanding</div>
            {(ar.by_customer || []).slice(0, 5).map((row) => {
              const pct = ar.total_outstanding > 0 ? (row.outstanding / ar.total_outstanding) * 100 : 0;
              const isOverdue = row.overdue > 0;
              return (
                <button
                  key={row.customer_id}
                  onClick={onRowClick}
                  className="w-full text-left group"
                  data-testid="ar-aging-row"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium truncate group-hover:text-[hsl(var(--primary))] transition-colors">
                        {row.customer_name}
                      </div>
                      <div className="text-[11px] text-[hsl(var(--text-muted))]">
                        {row.invoice_count} {row.invoice_count === 1 ? "invoice" : "invoices"}
                        {isOverdue && ` · ${row.oldest_days_overdue}d late`}
                      </div>
                    </div>
                    <div className="font-mono font-semibold text-[13px]">{gbp(row.outstanding)}</div>
                  </div>
                  <div className="mt-1 h-1 rounded-full bg-[hsl(var(--surface-hover))] overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${pct}%`,
                        background: isOverdue ? "#F43F5E" : "hsl(var(--primary))",
                      }}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}



function QuoteResponsesCard({ responses, onNav }) {
  const accepted = responses.filter((q) => q.status === "accepted");
  const declined = responses.filter((q) => q.status === "declined");
  const latest = responses[0];
  const isAccept = latest.status === "accepted";

  return (
    <Card data-testid="quote-responses-card">
      <div className="p-5">
        <div className="flex items-start gap-4">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${isAccept ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-rose-500/15 text-rose-600 dark:text-rose-400"}`}>
            {isAccept ? <CheckCircle2 className="w-5 h-5" strokeWidth={2}/> : <XCircle className="w-5 h-5" strokeWidth={2}/>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-[14px]">Quote responses</h3>
              {accepted.length > 0 && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">{accepted.length} accepted</span>}
              {declined.length > 0 && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-600 dark:text-rose-400">{declined.length} declined</span>}
            </div>
            <div className="text-[12px] text-[hsl(var(--text-muted))] mt-0.5">Latest activity from customer portals.</div>

            <div className="mt-3 space-y-2">
              {responses.map((q) => (
                <button
                  key={q.id}
                  onClick={onNav}
                  className="w-full text-left flex items-center gap-3 p-2.5 rounded-lg border border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--surface-hover))] transition-all"
                  data-testid="quote-response-row"
                >
                  {q.status === "accepted"
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" strokeWidth={2}/>
                    : <XCircle className="w-4 h-4 text-rose-600 dark:text-rose-400 shrink-0" strokeWidth={2}/>}
                  <FileText className="w-3.5 h-3.5 text-[hsl(var(--text-muted))] shrink-0" strokeWidth={1.8}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium truncate">
                      <span className="font-mono">{q.number}</span>
                      <span className="text-[hsl(var(--text-muted))]"> · </span>
                      <span>{q.customer_name}</span>
                    </div>
                    <div className="text-[11px] text-[hsl(var(--text-muted))] truncate">
                      {q.status === "accepted" ? "Accepted" : "Declined"}
                      {q.responded_by_name ? ` by ${q.responded_by_name}` : ""} · {new Date(q.responded_at).toLocaleString()}
                      {q.response_note ? `, "${q.response_note.slice(0, 60)}${q.response_note.length > 60 ? "…" : ""}"` : ""}
                    </div>
                  </div>
                  <span className="font-mono font-semibold text-[13px] shrink-0">
                    {gbp(q.setup_total || q.total || 0)}
                    {(q.monthly_total || 0) > 0 && <span className="ml-1 text-[hsl(var(--primary))] text-[11px]">+ {gbp(q.monthly_total)}/mo</span>}
                  </span>
                  <ArrowUpRight className="w-3.5 h-3.5 text-[hsl(var(--text-muted))] shrink-0" strokeWidth={1.8}/>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}


function RecentReferralsCard({ referrals, onOpen, onSeeAll }) {
  const isEmpty = referrals.length === 0;
  return (
    <Card data-testid="recent-referrals-card">
      <div className="p-5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))]">
            <Users className="w-5 h-5" strokeWidth={2}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-[14px]">Customer referrals</h3>
              {!isEmpty && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))]">{referrals.length} new</span>}
              <button onClick={onSeeAll} className="ml-auto text-[12px] text-[hsl(var(--primary))] font-semibold inline-flex items-center gap-1" data-testid="referrals-see-all">
                See all <ArrowUpRight className="w-3 h-3" strokeWidth={2}/>
              </button>
            </div>
            <div className="text-[12px] text-[hsl(var(--text-muted))] mt-0.5">Prospects sent in by your existing customers via the portal.</div>

            {isEmpty ? (
              <div className="mt-3 rounded-lg border border-dashed border-[hsl(var(--border))] p-4 text-center" data-testid="referrals-empty">
                <div className="text-[12.5px] font-semibold">No referrals yet.</div>
                <div className="text-[11.5px] text-[hsl(var(--text-muted))] mt-0.5 leading-snug max-w-md mx-auto">
                  When a customer fills in the "Register their interest" form on their dashboard, they'll appear here with the referrer's name attached.
                </div>
              </div>
            ) : (
              <div className="mt-3 space-y-2">
                {referrals.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => onOpen(r.id)}
                    className="w-full text-left flex items-center gap-3 p-2.5 rounded-lg border border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--surface-hover))] transition-all"
                    data-testid="referral-row"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium truncate">
                        <span className="font-semibold">{r.contact || r.company}</span>
                        {r.company && r.contact && <span className="text-[hsl(var(--text-muted))]"> · {r.company}</span>}
                      </div>
                      <div className="text-[11px] text-[hsl(var(--text-muted))] truncate">
                        {r.referred_by ? <>From <span className="font-semibold text-[hsl(var(--text))]">{r.referred_by}</span> · </> : null}
                        {new Date(r.created_at).toLocaleDateString()}
                        {r.email && <> · {r.email}</>}
                      </div>
                    </div>
                    <StatusPill variant={leadStageVariant(r.stage)}>{r.stage}</StatusPill>
                    <ArrowUpRight className="w-3.5 h-3.5 text-[hsl(var(--text-muted))] shrink-0" strokeWidth={1.8}/>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
